import AppDescription from '@nxsys/components/AppDescription';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import {
  defaultPaymentTypeSubOptions,
  defaultRepetitionOptions,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Deduction } from '@nxsys/store/slices/paymentSetting/types';
import { Icons } from '@nxsys/types/icons';
import { Descriptions } from 'antd';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DeductionForm, {
  DeductionFormRef,
  DeductionFormValues,
} from './DeductionForm';

export type EditDeductionProps = {
  isOpen: boolean;
  toggle: () => void;
  data: Deduction | null;
  onSubmit: (values: DeductionFormValues) => Promise<void>;
  paymentModel?: string;
  hideDates?: boolean;
};

const EditDeductionModal: React.FC<EditDeductionProps> = ({
  isOpen,
  toggle,
  data,
  onSubmit,
  paymentModel,
  hideDates,
}: EditDeductionProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isCISWorker = isCISPaymentModel(paymentModel);
  const isUmbrellaPAYE = UMBRELLA_PAYE_HYBRID.includes(paymentModel ?? '');

  const formRef = useRef<DeductionFormRef>(null);

  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: DeductionFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const descriptionItems = [
    {
      label: messages['paymentSetting.deduction.deductionCode'],
      value: data?.deductionCode ?? '',
    },
    {
      label: messages['paymentSetting.payment.description'],
      value: data?.description ?? '',
    },
    ...(isIreland
      ? [
          {
            label: messages['common.repetition'],
            value:
              defaultRepetitionOptions.find(
                (option) => option.value === data?.repetition,
              )?.label || '',
          },
        ]
      : []),
    ...(!isBureau
      ? [
          {
            label: messages['common.deductionType'],
            value:
              defaultPaymentTypeSubOptions.find(
                (option) => option.value === data?.deductionType,
              )?.label || '',
          },
        ]
      : []),
    {
      label: messages['paymentSetting.payment.minDeductionPeriod'],
      value: data?.minDeductionPeriod?.toString() || '-',
    },
    {
      label: messages['common.startDate'],
      value: data?.startDate
        ? moment(data?.startDate, 'MM-DD-YYYY').format('DD/MM/YYYY')
        : '-',
    },
    {
      label: messages['common.endDate'],
      value: data?.endDate
        ? moment(data?.endDate, 'MM-DD-YYYY').format('DD/MM/YYYY')
        : '-',
    },
    {
      label: messages['paymentSetting.payment.maxRecurrence'],
      value: data?.maxRecurrence?.toString() || '-',
    },
    {
      label: messages['common.totalDeduction'],
      value: data?.totalDeduction?.toString() || '-',
    },
    ...(!isIreland && isCISWorker
      ? [
          {
            label: messages['common.beforeCISTax'],
            value: data?.includeInCisCalculation
              ? messages['common.yes']
              : messages['common.no'],
          },
        ]
      : []),
    ...(!isIreland && isUmbrellaPAYE
      ? [
          {
            label: messages['common.beforeNI'],
            value: data?.beforeNI
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeTax'],
            value: data?.beforeTax
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforePension'],
            value: data?.beforePension
              ? messages['common.yes']
              : messages['common.no'],
          },
        ]
      : []),
    ...(isIreland
      ? [
          {
            label: messages['common.beforeTax'],
            value: data?.beforeTax
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeEmployeePRSI'],
            value: data?.employeePRSICanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeEmployerPRSI'],
            value: data?.employerPRSICanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeUSC'],
            value: data?.uscCanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeASC'],
            value: data?.ascCanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforePensionSchemeDeductions'],
            value: data?.pensionSchemaDeductionCanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['paymentSetting.payment.beforeLPT'],
            value: data?.lptCanBeDeducted
              ? messages['common.yes']
              : messages['common.no'],
          },
          {
            label: messages['common.beforeRCT'],
            value: data?.includeInCisCalculation
              ? messages['common.yes']
              : messages['common.no'],
          },
        ]
      : []),
  ];

  return (
    <AppModalContainer
      title={messages['paymentSetting.deduction.deductionDetail']}
      isOpen={isOpen}
      toggle={toggle}
      destroyOnClose
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {data && (
        <>
          {!isEditing ? (
            <AppDescription
              column={1}
              labelStyle={{ width: 216 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {descriptionItems?.map((item, _index) => (
                <Descriptions.Item key={_index} label={item.label} span={1}>
                  {item.value ?? '-'}
                </Descriptions.Item>
              ))}
            </AppDescription>
          ) : (
            <DeductionForm
              formRef={formRef}
              onSubmit={handleSubmit}
              paymentModel={paymentModel}
              hideDates={hideDates}
              defaultValues={{
                description: data?.description,
                includeInCisCalculation: data?.includeInCisCalculation,
                maxRecurrence: data?.maxRecurrence?.toString(),
                minDeductionPeriod: data?.minDeductionPeriod?.toString(),
                deductionCode: data?.deductionCode,
                deductionType:
                  defaultPaymentTypeSubOptions.find(
                    (option) => option.value === data?.deductionType,
                  ) || null,
                startDate: data?.startDate
                  ? moment(data?.startDate, 'MM-DD-YYYY')
                  : null,
                endDate: data?.endDate
                  ? moment(data?.endDate, 'MM-DD-YYYY')
                  : null,
                totalDeduction: data?.totalDeduction?.toString(),
                repetition:
                  defaultRepetitionOptions.find(
                    (option) => option.value === data?.repetition,
                  ) || null,
                beforeTax: data?.taxCanBeDeducted ?? data?.beforeTax,
                employeePRSICanBeDeducted: data?.employeePRSICanBeDeducted,
                employerPRSICanBeDeducted: data?.employerPRSICanBeDeducted,
                uscCanBeDeducted: data?.uscCanBeDeducted,
                ascCanBeDeducted: data?.ascCanBeDeducted,
                pensionSchemaDeductionCanBeDeducted:
                  data?.pensionSchemaDeductionCanBeDeducted,
                beforeNI: data?.beforeNI,
                beforePension: data?.beforePension,
                lptCanBeDeducted: data?.lptCanBeDeducted,
              }}
            />
          )}
        </>
      )}
    </AppModalContainer>
  );
};

export default EditDeductionModal;
